import { UIElement } from '../ui-element';
import { addLeadingZero, getDaysInMonth } from '../../global/helpers';
import { Locale } from '../../global/enums';
import type { NullOr } from '../../global/utils.type';
import type {
    UIDateElementControl,
    UIDateOffset,
    UIDateParts,
    UIDateValueResult,
    UIMonthValueResult,
    UITimeValueResult,
    UIYearPeriod,
} from './ui-dateelement.type';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @classdesc Abstract class for date elements. Holds date and methods to
 * work with it. Usually used in combination with other date-related elements.
 * @alias UIDateElement
 * @property {string} max {@attr max} Maximum date value. String like in format
 * '(-|+)N(m|d|y)'. Where N is integer for offset. 'd' - modifier day,
 * 'm' - modifier for month, 'y' - modifier for years.
 * @property {string} min {@attr min} Minimum date value. String like in format
 * '(-|+)N(m|d|y)'. Where N is integer for offset. 'd' - modifier day,
 * 'm' - modifier for month, 'y' - modifier for years.
 * @property {Locale} [locale="en"] {@attr locale} Localization rules in "lt-LT", "et-EE",
 * "lv-LV" and "iso8601 formats.
 * @property {UIDateElement | HTMLInputElement | null} control
 * @property {string} value
 */
export class UIDateElement extends UIElement {
    declare public control: NullOr<UIDateElementControl | HTMLInputElement>;
    protected _value: NullOr<string> = null;

    get value(): NullOr<string> {
        return this._value;
    }

    set value(value: NullOr<string>) {
        this._value = value;
    }

    static readonly PREDEFINED_DATES = {
        tomorrow: '+1d',
        yesterday: '-1d',
        today: '0d',
    } as const;

    /**
     * Types of input.
     * @type {{MONTH: string, YEAR: string, TIME: string}}
     */
    static readonly VALUE_TYPES = {
        YEAR: 'year',
        MONTH: 'month',
        TIME: 'time',
    } as const;

    protected static readonly LithuanianLocales = [
        Locale.LegacyLT,
        Locale.Lithuania,
        Locale.ISO8601,
    ];

    /**
     * Gets Monday date of a week of a input date.
     * @param {Date} date
     * @returns {Date}
     */
    static getMonday(date: Date): Date {
        const diff =
            date.getDate() - date.getDay() + (date.getDay() === 0 ? -6 : 1);
        return new Date(date.setDate(diff));
    }

    /**
     * Gets the 1st of January of last year.
     * @param {Date} date
     * @returns {{start: Date, end: Date}}
     */
    static getLastYearPeriod(date: Date): UIYearPeriod {
        const lastYear = date.getFullYear() - 1;
        return {
            start: new Date(lastYear, 0, 1),
            end: new Date(lastYear, 11, 31),
        };
    }

    get valueType(): string {
        return UIDateElement.VALUE_TYPES.YEAR;
    }

    // ^ Start of the line
    // `(1[0-2] or [1-9])`
    // :A literal colon : separating hours and minutes.
    // ([0-5][0-9])	Minute part: a digit 0–5 followed by any digit 0–9, i.e., 00 to 59.
    // \s* Optional whitespace (zero or more spaces or tabs).
    // ([apAP][mM])	AM/PM part: an a or p (case-insensitive) followed by m or M.
    // $ End of the line
    get hours12RegExp(): RegExp {
        return /^(1[0-2]|[1-9]):([0-5][0-9])\s*([apAP][mM])$/;
    }

    // The regular expression ([0-1]?[0-9]|2[0-3]):([0-5][0-9]) is designed to match a
    // valid 24-hour time format (HH:MM), where:
    // HH represents the hours (from 00 to 23)
    // MM represents the minutes (from 00 to 59)
    // and special business case 24:00.
    get hours24RegExp(): RegExp {
        return /^((([0-1]?[0-9]|2[0-3]):([0-5][0-9]))|(24:00))$/;
    }

    get dateRegExp(): RegExp {
        if (UIDateElement.LithuanianLocales.includes(this.locale)) {
            return /^[0-9]{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;
        }
        return /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.[0-9]{4}$/;
    }

    // ^ Start of line
    // [0-9]{4} four digits
    // - dash
    // [0-9]{2} two digits
    // $ End of the line.
    get monthRegExp(): RegExp {
        return /^[0-9]{4}-[0-9]{1,2}$/;
    }

    /**
     * Regular expression to parse date difference like '-4d', '+3d'.
     * ^ Start of the line
     * - [-+]? zero or one plus (+) or (-) minus symbol.
     * \d+ one or more digits
     * [dmy] any character of d m y
     * $ End of the line
     * @returns {RegExp}
     */
    get dayOffsetRegExp(): RegExp {
        return /^([-+]?)(\d+)([dmy])$/;
    }

    private get _control(): UIDateElementControl {
        return this.control;
    }

    get max(): NullOr<string> {
        const value = this._control?.getAttribute('max') ?? null;
        return this.isValidValue(value) ? value : null;
    }

    set max(value: NullOr<string>) {
        if (!this._control || !this.isValidValue(value)) {
            return;
        }
        this._control.setAttribute('max', value);
    }

    get min(): NullOr<string> {
        const value = this._control?.getAttribute('min') ?? null;
        return this.isValidValue(value) ? value : null;
    }

    set min(value: NullOr<string>) {
        if (!this._control || !this.isValidValue(value)) {
            return;
        }
        this._control.setAttribute('min', value);
    }

    get locale(): Locale {
        if (this.hasAttribute('locale')) {
            return this.getAttribute('locale') as Locale;
        }
        return UIElement.LOCALE;
    }

    set locale(value: Locale) {
        this.setAttribute('locale', value);
    }

    get _timestamp(): number {
        const value = this.value;
        if (this.isValidValue(value)) {
            return this.dateStringToTimestamp(value);
        }
        return NaN;
    }

    /**
     * Used in date-range like elements to synchronize the controls.
     */
    syncControls(): void {
        /* intentionally empty */
    }

    /**
     * Parses a string in format '(-|+)N(m|d|y)', where N is integer for offset (eg. '-4d', '+3m', '+1y').
     * @param {string} value
     * @returns {{value: number, part: 'd' | 'm' | 'y'} | null}
     */
    parseDateOffset(value: string): NullOr<UIDateOffset> {
        const result = String(value).match(this.dayOffsetRegExp);
        if (result) {
            const part = result[3] as UIDateOffset['part'];
            const val = parseInt(result[2]);
            if (Number.isNaN(val)) {
                return null;
            }
            return {
                value: parseInt(String(result[1] === '-' ? -val : val)), // Get rid of negative zero.
                part: part,
            };
        }
        return null;
    }

    /**
     * Checks if given string matched to validation rules for time value
     * @param {string} value
     * @returns {boolean}
     */
    isValidTime(value: NullOr<string>): boolean {
        return (
            this.hours24RegExp.test(value ?? '') ||
            this.hours12RegExp.test(value ?? '')
        );
    }

    /**
     * Checks if given string matched to validation rules for date value
     * @param {string} value
     * @param {boolean} [allowEmpty]
     * @returns {boolean}
     */
    isValidDate(value: NullOr<string>, allowEmpty: boolean = false): boolean {
        if (allowEmpty && !value) {
            return true;
        }

        if (!value) {
            return false;
        }

        if (['today', 'yesterday', 'tomorrow'].includes(value)) {
            return true;
        }

        if (this.dayOffsetRegExp.test(value)) {
            return true;
        }

        if (!this.dateRegExp.test(value)) {
            return false;
        }

        const parts = this.parseDateString(value);
        const date = new Date(parts.year, parts.month - 1, parts.date);

        return (
            date.getFullYear() === parts.year &&
            date.getMonth() === parts.month - 1 &&
            date.getDate() === parts.date
        );
    }

    /**
     * Checks if given string matched to validation rules for month value
     * @param {string} value
     * @returns {boolean}
     */
    isValidMonth(value: NullOr<string>): boolean {
        return this.monthRegExp.test(value ?? '');
    }

    /**
     * Checks if given value belongs to date range
     * @param {Date} date
     * @returns {boolean}
     */
    isDateInRange(date: Date): boolean {
        const minDateValue = this.getMinDate();
        const maxDateValue = this.getMaxDate();
        if (minDateValue && minDateValue > date) {
            return false;
        }
        return !(maxDateValue && maxDateValue < date);
    }

    /**
     * Parse date string based on locale
     * @param {string} value
     * @returns {object}
     */
    parseDateString(value: string): UIDateParts {
        const date = new Date();
        let m: number;
        let y: number;
        let daysInMonth: number;

        const predefinedDate =
            UIDateElement.PREDEFINED_DATES[
                value as keyof typeof UIDateElement.PREDEFINED_DATES
            ];
        if (predefinedDate !== undefined) {
            value = predefinedDate;
        }

        const offset = this.parseDateOffset(value);
        if (offset) {
            switch (offset.part) {
                case 'y':
                    date.setFullYear(date.getFullYear() + offset.value);
                    break;
                case 'm':
                    // This is JS Date() object bug. Check for days count manually.
                    // When changing the month if the previous days count less than current month
                    // current month is selected.
                    m = date.getMonth() + offset.value;
                    y = date.getFullYear();
                    daysInMonth = getDaysInMonth(y, m);
                    if (date.getDate() > daysInMonth) {
                        date.setDate(daysInMonth);
                    }
                    date.setMonth(m);
                    break;
                default:
                    date.setDate(date.getDate() + offset.value);
                    break;
            }

            return {
                date: date.getDate(),
                month: date.getMonth() + 1,
                year: date.getFullYear(),
            };
        }

        const sep =
            UIDateElement.LithuanianLocales.includes(this.locale) ||
            this.valueType === UIDateElement.VALUE_TYPES.MONTH
                ? '-'
                : '.';
        const parts = value.split(sep).map(Number);

        if (
            UIDateElement.LithuanianLocales.includes(this.locale) ||
            this.valueType === UIDateElement.VALUE_TYPES.MONTH
        ) {
            return {
                date: parts[2],
                month: parts[1],
                year: parts[0],
            };
        }

        return {
            date: parts[0],
            month: parts[1],
            year: parts[2],
        };
    }

    /**
     * Convert to date string based on locale.
     * @param {number} date
     * @param {number} month
     * @param {number} year
     * @returns {string} Returns date string in format 'dd.mm.yyyy' or 'yyyy-mm-dd' based on locale.
     */
    formatDateString(
        date: number | string,
        month: number | string,
        year: number
    ): string {
        const retval = [year, addLeadingZero(month)];
        if (date) {
            retval.push(addLeadingZero(date));
        }
        if (UIDateElement.LithuanianLocales.includes(this.locale)) {
            return retval.join('-');
        }
        retval.reverse();
        return retval.join('.');
    }

    /**
     * Converts to date string from UNIX timestamp.
     * @param {number} ts
     * @returns {string}
     */
    timestampToDateString(ts: number): string {
        const d = new Date(ts);
        return this.formatDateString(
            d.getDate(),
            d.getMonth() + 1,
            d.getFullYear()
        );
    }

    /**
     * Converts string date to timestamp.
     * @param {string} dateString
     * @returns {number}
     */
    dateStringToTimestamp(dateString: string): number {
        if (!this.isValidDate(dateString)) {
            return 0;
        }
        const parts = this.parseDateString(dateString);
        const date = new Date(
            parts.year,
            parts.month - 1,
            parts.date,
            0,
            0,
            0,
            0
        );
        const userTimezoneOffset = date.getTimezoneOffset() * 60000;
        return date.valueOf() - userTimezoneOffset;
    }

    /**
     * Converts string date to timestamp using UTC date.
     * @param {string} dateString
     * @returns {number}
     */
    dateStringToTimestampUTC(dateString: string): number {
        if (!this.isValidDate(dateString)) {
            return 0;
        }
        const parts = this.parseDateString(dateString);
        return new Date(
            Date.UTC(parts.year, parts.month - 1, parts.date, 0, 0, 0, 0)
        ).getTime();
    }

    /**
     * Reformat given value, converts constants like 'today/tomorrow/yesterday' to real dates.
     * @param {string} str
     * @returns {string}
     */
    reformatDateString(str: string): string {
        if (!str) {
            return '';
        }
        const value = this.parseDateString(str);
        return this.formatDateString(value.date, value.month, value.year);
    }

    /**
     * Convert to date string based on locale.
     * @param {number} month
     * @param {number} year
     * @returns {string}
     */
    formatMonthString(month: number, year: number): string {
        return [year, addLeadingZero(month)].join('-');
    }

    /**
     * Returns the min date in Date representation
     * with hours, minutes, seconds and milliseconds set to zero.
     * @returns {Date} date
     */
    getMinDate(): NullOr<Date> {
        if (!this.min) {
            return null;
        }
        const parts = this.parseDateString(this.min);
        return new Date(parts.year, parts.month - 1, parts.date);
    }

    /**
     * Returns the max date in Date representation
     * with hours, minutes, seconds and milliseconds set to zero.
     * @returns {Date} date
     */
    getMaxDate(): NullOr<Date> {
        if (!this.max) {
            return null;
        }
        const parts = this.parseDateString(this.max);
        return new Date(parts.year, parts.month - 1, parts.date);
    }

    /**
     * Parse date string and shift values to be in range if needed.
     * @param {string} value
     * @param {string} min
     * @param {string} max
     * @returns {object}
     */
    parseDateValue(
        value: string,
        min: NullOr<string>,
        max: NullOr<string>
    ): UIDateValueResult {
        const parts = this.parseDateString(
            this.isValidDate(value) ? value : 'today'
        );

        let dayValue = parts.date;
        let monthValue = parts.month;
        let yearValue = parts.year;

        if (min) {
            const minParts = this.parseDateString(min);
            const minDayValue = minParts.date;
            const minMonthValue = minParts.month;
            const minYearValue = minParts.year;
            if (
                yearValue < minYearValue ||
                (yearValue === minYearValue && monthValue < minMonthValue) ||
                (yearValue === minYearValue &&
                    monthValue === minMonthValue &&
                    dayValue < minDayValue)
            ) {
                dayValue = minDayValue;
                yearValue = minYearValue;
                monthValue = minMonthValue;
            }
        }

        if (max) {
            const maxParts = this.parseDateString(max);
            const maxDayValue = maxParts.date;
            const maxMonthValue = maxParts.month;
            const maxYearValue = maxParts.year;
            if (
                yearValue > maxYearValue ||
                (yearValue === maxYearValue && monthValue > maxMonthValue) ||
                (yearValue === maxYearValue &&
                    monthValue === maxMonthValue &&
                    dayValue > maxDayValue)
            ) {
                dayValue = maxDayValue;
                yearValue = maxYearValue;
                monthValue = maxMonthValue;
            }
        }

        return {
            value: this.formatDateString(
                dayValue < 10 ? '0' + dayValue : dayValue,
                monthValue < 10 ? '0' + monthValue : monthValue,
                yearValue
            ),
            year: yearValue,
            month: monthValue,
            day: dayValue,
        };
    }

    /**
     * Parse month string and shift values to be in range if needed.
     * @param {string} value
     * @param {string} min
     * @param {string} max
     * @returns {object}
     */
    parseMonthValue(
        value: string,
        min: NullOr<string>,
        max: NullOr<string>
    ): UIMonthValueResult {
        let values;
        if (!this.isValidMonth(value)) {
            const date = new Date();
            values = [date.getFullYear(), date.getMonth() + 1];
        } else {
            values = value.split('-').map(Number);
        }

        let yearValue = values[0];
        let monthValue = values[1];

        if (min) {
            const minParts = min.split('-').map(Number);
            const minYearValue = minParts[0];
            const minMonthValue = minParts[1];
            if (
                yearValue < minYearValue ||
                (yearValue === minYearValue && monthValue < minMonthValue)
            ) {
                yearValue = minYearValue;
                monthValue = minMonthValue;
            }
        }

        if (max) {
            const maxParts = max.split('-').map(Number);
            const maxYearValue = maxParts[0];
            const maxMonthValue = maxParts[1];
            if (
                yearValue > maxYearValue ||
                (yearValue === maxYearValue && monthValue > maxMonthValue)
            ) {
                yearValue = maxYearValue;
                monthValue = maxMonthValue;
            }
        }

        return {
            value: [
                yearValue,
                monthValue < 10 ? addLeadingZero(monthValue) : monthValue,
            ].join('-'),
            year: yearValue,
            month: monthValue,
        };
    }

    /**
     * Parse time string and shift values to be in range if needed.
     * @param {string} value
     * @param {string} min
     * @param {string} max
     * @returns {object}
     */
    parseTimeValue(
        value: string,
        min: NullOr<string>,
        max: NullOr<string>
    ): UITimeValueResult {
        let values: [number, number];
        if (!this.isValidTime(value)) {
            values = [0, 0];
        } else {
            values = this.convertTimeTo24Hours(value)
                .split(':')
                .map(Number) as [number, number];
        }

        let hoursVal = values[0];
        let minutesVal = values[1];

        if (min) {
            const minParts = min.split(':').map(Number) as [number, number];
            const minHoursVal = minParts[0];
            const minMinutesVal = minParts[1];
            if (
                hoursVal < minHoursVal ||
                (hoursVal === minHoursVal && minutesVal < minMinutesVal)
            ) {
                hoursVal = minHoursVal;
                minutesVal = minMinutesVal;
            }
        }

        if (max) {
            const maxParts = max.split(':').map(Number) as [number, number];
            const maxHoursVal = maxParts[0];
            const maxMinutesVal = maxParts[1];
            if (
                hoursVal > maxHoursVal ||
                (hoursVal === maxHoursVal && minutesVal > maxMinutesVal)
            ) {
                hoursVal = maxHoursVal;
                minutesVal = maxMinutesVal;
            }
        }

        return {
            value: [
                hoursVal < 10 ? '0' + hoursVal : hoursVal,
                minutesVal < 10 ? '0' + minutesVal : minutesVal,
            ].join(':'),
            hours: hoursVal,
            minutes: minutesVal,
        };
    }

    /**
     * Validate control value based on value type.
     * @param {string} value
     * @returns {boolean}
     */
    isValidValue(value: NullOr<string>): value is string {
        switch (this.valueType) {
            case UIDateElement.VALUE_TYPES.TIME:
                return this.isValidTime(value);
            case UIDateElement.VALUE_TYPES.MONTH:
                return this.isValidMonth(value);
            case UIDateElement.VALUE_TYPES.YEAR:
            default:
                return this.isValidDate(value);
        }
    }

    /**
     * Converts value to 24-hours format.
     * @param {string} value
     * @returns {string}
     */
    convertTimeTo24Hours(value: string): string {
        if (this.hours24RegExp.test(value)) {
            return value;
        }

        if (!this.hours12RegExp.test(value)) {
            return '00:00';
        }
        const matches = value.match(this.hours12RegExp)!;
        let hours = Number(matches[1]);
        const minutes = Number(matches[2]);
        const modifier = matches[3].toLowerCase();
        if (modifier === 'am' && hours === 12) {
            hours = 0;
        }
        if (modifier === 'pm' && hours < 12) {
            hours += 12;
        }
        return [
            hours < 10 ? '0' + hours : hours,
            minutes < 10 ? '0' + minutes : minutes,
        ].join(':');
    }
}
